import { IEntityState } from '@core/state/entities/entity';

/**
 * Structural view of one project-scoped job row.
 *
 * `stateRevision` is deliberately optional: the generated DTOs gain the field per family as the
 * matching server contract lands, so this helper has to compile on both sides of a rolling
 * deployment (the same technique `bulk-pdf-upload-job.status.ts` uses for `consumesActiveCapacity`).
 * A missing or non-finite revision reads as `0` — "unknown, written before the field existed" —
 * which sorts below every real revision without pretending to be the oldest real one.
 */
export interface ProjectJobLike {
  id: string;
  projectId: string;
  stateRevision?: number | null;
}

/**
 * Describes one job family to the shared reconciliation functions.
 *
 * `entityKey` and `membershipField` happen to be spelled the same for today's three families, but
 * they are different concepts (normalized entity map vs. ProjectDetails foreign-key array) and are
 * named separately so a future family that breaks the coincidence cannot silently mis-reconcile.
 */
export interface ProjectJobFamily {
  /** Normalized entity map key, e.g. `entityKey.searchImportJobs`. */
  readonly entityKey: string;
  /** ProjectDetails membership array holding this family's ids. */
  readonly membershipField: string;
  /**
   * `permissionReport.project` keys, any of which authorises viewing this family. The server owns
   * the real gate and sends an empty collection when it denies access; the client mirrors the gate
   * only so it can tell "you may not see these" apart from "there are none".
   */
  readonly viewPermissions: readonly string[];
}

export const searchImportJobFamily: ProjectJobFamily = {
  entityKey: 'searchImportJobs',
  membershipField: 'searchImportJobs',
  viewPermissions: ['viewSearches', 'importSearch'],
};

export const bulkStudyUpdateJobFamily: ProjectJobFamily = {
  entityKey: 'bulkStudyUpdateJobs',
  membershipField: 'bulkStudyUpdateJobs',
  viewPermissions: ['viewStudies', 'bulkUpdateStudies'],
};

export const bulkPdfUploadJobFamily: ProjectJobFamily = {
  entityKey: 'bulkPdfUploadJobs',
  membershipField: 'bulkPdfUploadJobs',
  viewPermissions: ['bulkPdfUpload'],
};

/** Every family whose ProjectDetails membership array is reconciled by project audit version. */
export const projectJobFamilies: readonly ProjectJobFamily[] = [
  bulkPdfUploadJobFamily,
  searchImportJobFamily,
  bulkStudyUpdateJobFamily,
];

/**
 * Deliberately narrow structural views: they must accept the real normalized entity types, which
 * carry no index signature, so the dynamic membership/entity-map lookups are done through explicit
 * casts rather than by widening these declarations.
 */
export interface ProjectDetailsSnapshotView {
  permissionReport?: {
    project?: Record<string, boolean> | null;
  } | null;
}

interface ProjectJobSnapshotEntities {
  projects?: Record<
    string,
    { audit?: { version?: number | null } | null } | null
  >;
  projectDetails?: Record<string, ProjectDetailsSnapshotView | null>;
}

export interface NormalizedProjectJobSnapshotPayload {
  projectId?: string;
  entities: ProjectJobSnapshotEntities;
}

export interface ProjectJobSnapshot<TJob extends ProjectJobLike> {
  projectId: string;
  projectVersion: number;
  authorized: boolean;
  jobIds: string[];
  jobs: Record<string, TJob>;
}

/** Entity state plus the per-slice reconciliation bookkeeping. Each reducer owns its own slice. */
export interface ProjectJobFamilyState<
  TJob extends ProjectJobLike,
> extends IEntityState<TJob> {
  /** Highest whole-Project audit version reconciled for this family's membership, per project. */
  projectJobSnapshotVersions?: Readonly<Record<string, number>>;
  /** Client-side terminal fence: a deleted Project id can never be resurrected by late payloads. */
  deletedProjectIds?: Readonly<Record<string, true>>;
}

/** Legacy/unknown revisions read as 0 so they sort below every server-written revision. */
export function readProjectJobStateRevision(job: ProjectJobLike): number {
  return typeof job.stateRevision === 'number' &&
    Number.isFinite(job.stateRevision)
    ? job.stateRevision
    : 0;
}

export function readProjectJobPayloadProjectId(
  payload: NormalizedProjectJobSnapshotPayload
): string | null {
  const projectDetails = payload.entities.projectDetails;
  return (
    payload.projectId ??
    (projectDetails && Object.keys(projectDetails).length === 1
      ? Object.keys(projectDetails)[0]
      : null)
  );
}

/**
 * The Project aggregate's audit version, or null when this payload carries no Project row. Without
 * it the payload cannot order membership at all — only the per-job revisions remain usable.
 */
export function readProjectSnapshotVersion(
  payload: NormalizedProjectJobSnapshotPayload,
  projectId: string
): number | null {
  const version = payload.entities.projects?.[projectId]?.audit?.version;
  return Number.isFinite(version) ? (version as number) : null;
}

export function isProjectJobFamilyAuthorized(
  family: ProjectJobFamily,
  permissions: Record<string, boolean> | null | undefined
): boolean {
  return family.viewPermissions.some((key) => permissions?.[key] === true);
}

/**
 * Reads this family's normalized entity map. The parameter is deliberately looser than
 * `NormalizedProjectJobSnapshotPayload`: single-job actions carry only their own entity map, and
 * that payload type is a weak type (every property optional) which such an action cannot satisfy.
 */
export function readIncomingProjectJobs<TJob extends ProjectJobLike>(
  family: ProjectJobFamily,
  payload: { entities: object }
): Record<string, TJob> | undefined {
  return (payload.entities as Record<string, unknown>)[family.entityKey] as
    | Record<string, TJob>
    | undefined;
}

/**
 * A Project aggregate's audit version is the ordering token shared by HTTP and SignalR payloads.
 * The per-job revision orders updates within that aggregate snapshot, but cannot prove whether an
 * omitted id was pruned or merely came from an older whole-Project response.
 */
export function readProjectJobSnapshot<TJob extends ProjectJobLike>(
  family: ProjectJobFamily,
  payload: NormalizedProjectJobSnapshotPayload
): ProjectJobSnapshot<TJob> | null {
  const projectId = readProjectJobPayloadProjectId(payload);
  if (!projectId) {
    return null;
  }

  const details = payload.entities.projectDetails?.[projectId];
  const projectVersion = readProjectSnapshotVersion(payload, projectId);
  if (!details || projectVersion === null) {
    return null;
  }

  const authorized = isProjectJobFamilyAuthorized(
    family,
    details.permissionReport?.project
  );
  const membership = (details as unknown as Record<string, unknown>)[
    family.membershipField
  ] as string[] | null | undefined;
  return {
    projectId,
    projectVersion,
    authorized,
    // The server's permission filter deliberately sends an empty collection when access is
    // denied. Do not allow an inconsistent normalized payload to retain ids in that case.
    jobIds: authorized ? [...(membership ?? [])] : [],
    jobs: authorized
      ? (readIncomingProjectJobs<TJob>(family, payload) ?? {})
      : {},
  };
}

/**
 * True when the stored row must be kept in preference to the incoming one.
 *
 * A versioned row (revision >= 1) is never overwritten by an unknown or lower revision, and two
 * equal versioned revisions carry no new information. Two *unknown* revisions carry no ordering at
 * all, though, so the later write wins there — that is the pre-revision behaviour, and keeping it
 * is what lets a legacy job with no revision still progress through its statuses.
 */
export function keepsExistingProjectJob(
  existing: ProjectJobLike,
  incoming: ProjectJobLike
): boolean {
  const existingRevision = readProjectJobStateRevision(existing);
  return (
    existingRevision > 0 &&
    existingRevision >= readProjectJobStateRevision(incoming)
  );
}

/** Drops every row this state holds for one project, leaving other projects untouched. */
export function removeProjectJobsForProject<TJob extends ProjectJobLike>(
  state: ProjectJobFamilyState<TJob>,
  projectId: string
): ProjectJobFamilyState<TJob> {
  const retainedIds = state.allIds.filter(
    (id) => state.byId[id]?.projectId !== projectId
  );
  if (retainedIds.length === state.allIds.length) {
    return state;
  }
  return {
    ...state,
    byId: Object.fromEntries(
      retainedIds.map((id) => [id, state.byId[id]])
    ) as Record<string, TJob>,
    allIds: retainedIds,
  };
}

/**
 * Project payloads can arrive out of order: a normal HTTP response may race a newer SignalR
 * projection. Job revisions are server-owned, so merge them independently by job id and never let
 * an older (or merely incomplete) project snapshot regress or remove one. Explicit project
 * deletion remains the sole aggregate-level removal path.
 */
export function mergeMonotonicProjectJobs<TJob extends ProjectJobLike>(
  state: ProjectJobFamilyState<TJob>,
  incomingJobs: Record<string, TJob> | null | undefined,
  existingOnly = false
): ProjectJobFamilyState<TJob> {
  if (!incomingJobs) {
    return state;
  }

  let byId = state.byId;
  let allIds = state.allIds;
  let changed = false;

  for (const [id, incoming] of Object.entries(incomingJobs)) {
    const existing = state.byId[id];
    if (existingOnly && !existing) {
      continue;
    }
    if (existing && keepsExistingProjectJob(existing, incoming)) {
      continue;
    }

    if (!changed) {
      byId = { ...state.byId };
      allIds = [...state.allIds];
      changed = true;
    }
    byId[id] = incoming;
    if (!existing) {
      allIds.push(id);
    }
  }

  return changed ? { ...state, byId, allIds } : state;
}

/**
 * True when this snapshot must not be allowed to change membership or authorization for the
 * family: it is strictly older than the last accepted one, or it is an equal-version *grant*
 * (an equal-version denial is the authoritative resolution of the same aggregate version and has
 * already been applied, so a delayed grant at that version cannot undo it).
 */
export function isStaleProjectJobSnapshot(
  knownVersion: number | undefined,
  projectVersion: number,
  authorized: boolean
): boolean {
  return (
    knownVersion !== undefined &&
    (projectVersion < knownVersion ||
      (projectVersion === knownVersion && authorized))
  );
}

export function reconcileProjectJobSnapshot<TJob extends ProjectJobLike>(
  family: ProjectJobFamily,
  state: ProjectJobFamilyState<TJob>,
  payload: NormalizedProjectJobSnapshotPayload
): ProjectJobFamilyState<TJob> {
  const payloadProjectId = readProjectJobPayloadProjectId(payload);
  if (payloadProjectId && state.deletedProjectIds?.[payloadProjectId]) {
    return state;
  }

  const snapshot = readProjectJobSnapshot<TJob>(family, payload);
  if (!snapshot) {
    const details = payloadProjectId
      ? payload.entities.projectDetails?.[payloadProjectId]
      : undefined;
    if (
      payloadProjectId &&
      details &&
      !isProjectJobFamilyAuthorized(family, details.permissionReport?.project)
    ) {
      // An unversioned payload cannot prove membership, but a denial is not a membership claim:
      // the server is saying this principal may not see the family at all. Like the equal-version
      // denial, the denial wins, so the project's rows go rather than lingering behind the gate.
      return removeProjectJobsForProject(state, payloadProjectId);
    }
    const hasAcceptedProjectSnapshot =
      payloadProjectId !== null &&
      state.projectJobSnapshotVersions?.[payloadProjectId] !== undefined;
    return mergeMonotonicProjectJobs(
      state,
      readIncomingProjectJobs<TJob>(family, payload),
      hasAcceptedProjectSnapshot
    );
  }

  const knownVersion = state.projectJobSnapshotVersions?.[snapshot.projectId];
  if (
    isStaleProjectJobSnapshot(
      knownVersion,
      snapshot.projectVersion,
      snapshot.authorized
    )
  ) {
    // A delayed HTTP response may arrive after a newer SignalR projection. Its included jobs can
    // still update retained ids by their own revision, but cannot restore ids that the newer
    // membership/authorization snapshot deliberately pruned.
    return mergeMonotonicProjectJobs(state, snapshot.jobs, true);
  }

  const retainedIds = state.allIds.filter(
    (id) => state.byId[id]?.projectId !== snapshot.projectId
  );
  const byId = Object.fromEntries(
    retainedIds.map((id) => [id, state.byId[id]])
  ) as Record<string, TJob>;
  const incomingIds: string[] = [];

  for (const id of snapshot.jobIds) {
    const incoming = snapshot.jobs[id];
    const existing = state.byId[id];
    const job =
      existing && incoming && keepsExistingProjectJob(existing, incoming)
        ? existing
        : (incoming ?? existing);
    if (!job || job.projectId !== snapshot.projectId) {
      continue;
    }
    byId[id] = job;
    incomingIds.push(id);
  }

  return {
    ...state,
    byId,
    allIds: [...retainedIds, ...incomingIds],
    projectJobSnapshotVersions: {
      ...state.projectJobSnapshotVersions,
      [snapshot.projectId]: snapshot.projectVersion,
    },
  };
}

/**
 * Project audit versions are only comparable within one effective principal. Keep the read-only
 * entities available for the permission selectors, but make the next payload from the new
 * principal authoritative even when its project version is unchanged.
 */
export function resetProjectJobSnapshotVersions<
  TState extends {
    projectJobSnapshotVersions?: Readonly<Record<string, number>>;
  },
>(state: TState): TState {
  return { ...state, projectJobSnapshotVersions: undefined };
}

/** Records the terminal deletion fence so late payloads can never resurrect the project's jobs. */
export function fenceDeletedProject<
  TState extends { deletedProjectIds?: Readonly<Record<string, true>> },
>(
  previous: TState,
  next: TState,
  projectId: string,
  terminalPhysicalDeletion: boolean | undefined
): TState {
  return {
    ...next,
    deletedProjectIds: terminalPhysicalDeletion
      ? { ...previous.deletedProjectIds, [projectId]: true }
      : previous.deletedProjectIds,
  };
}
